import { CONTENT_WIDTH, bottomMarginItemGlobal } from "../../utils/pdfConstants";
import { getRakennusluvatByKiinteisto } from "../../../api/api";

// Same single-composited-canvas-image technique as
// RenderMaaraaikaistarkastusTaulukko.js / renderCapexYhteenveto.js — the
// standard approach for any PDF block with a chart or table. This table is
// flat (no groups), but its row count is still unbounded (user-added), so
// it's chunked into several images the same way, each capped at a safe
// single-page height with the column header repeated at the top of every
// chunk, relying on pdfmake's own auto-flow onto a new page when a chunk
// doesn't fit the remaining space (no forced pageBreak — see the sibling
// file's history for why that was tried and reverted).

// Master canvas is ≈ CONTENT_WIDTH (538.58pt) × 2, matching every other
// composited-canvas PDF block in this app.
const IMG_WIDTH = 1080;
// Flush to the content edges (no horizontal pad) — the original pdfmake
// table had paddingLeft/Right: 0, same as every other left-aligned element
// on the page; a nonzero PAD_X would visibly shift the table right.
const PAD_X = 0;
const PAD_Y = 8;
const USABLE_W = IMG_WIDTH - PAD_X * 2;

// [Lupatunnus, Tyyppi / kuvaus, Myönnetty, Huomiot] — proportioned from the
// old pdfmake table's own widths ([70, "*", 60, "*"] pt, the two "*"
// columns splitting the remainder equally), scaled to px. Lupatunnus
// widened 140→190 — same bug class as RenderMaaraaikaistarkastusTaulukko.js's
// own history: a permit code like "LP-092-2019-07" is one unbreakable
// string with no space for wrapLines to split on, and it didn't fit 140px's
// wrap budget, so it rendered full-width and bled into Tyyppi / kuvaus next
// to it. Funded from Tyyppi (410→380) and the Huomiot remainder, both of
// which have slack for their typically short content.
const COL_LABELS = ["Lupatunnus", "Tyyppi / kuvaus", "Myönnetty", "Huomiot"];
// Last column absorbs whatever's left of USABLE_W, so the columns always
// sum exactly to it regardless of PAD/IMG_WIDTH tuning.
const COL_WIDTHS_FIXED = [190, 380, 120];
const COL_WIDTHS = [...COL_WIDTHS_FIXED, USABLE_W - COL_WIDTHS_FIXED.reduce((a, b) => a + b, 0)];

const CELL_PAD_X = 8;
const CELL_PAD_Y = 7;
// Guaranteed sliver of whitespace at the right of every cell regardless of
// content length — short single-word values never break mid-word, so
// without this a column only just wide enough for its text can bleed into
// its neighbor (see RenderMaaraaikaistarkastusTaulukko.js's own history).
const COL_GUTTER = 6;
const LINE_H = 23; // 17px table font +20%, on top of the original 12→17px match to renderToimenpiteetTekstimuodossa.js
const LINE_H_SMALL = 19;

// A full, empty A4 page's content height is ≈745pt (841.89 page height −
// 56.7 top margin − 39.69 bottom margin); 700pt stays safely under that so
// a chunk always eventually fits on some page (its own fresh one, if not
// the current one) — pdfmake auto-flows a sequence of image nodes onto a
// new page when one doesn't fit the remaining space, same as every other
// image/chart block in this codebase relies on.
const MAX_CHUNK_H = Math.round(700 * (IMG_WIDTH / CONTENT_WIDTH));

function wrapLines(ctx, text, maxWidth, font) {
  ctx.font = font;
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(test).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function measureHeader(ctx) {
  const font = "bold 20px Lato, sans-serif";
  let maxLines = 1;
  const lines = COL_LABELS.map((label, i) => {
    const l = wrapLines(ctx, label, COL_WIDTHS[i] - CELL_PAD_X * 2 - COL_GUTTER, font);
    maxLines = Math.max(maxLines, l.length);
    return l;
  });
  return { lines, height: maxLines * LINE_H + CELL_PAD_Y * 2 };
}

function measureRow(ctx, row) {
  const [wLupatunnus, wTyyppi, wMyonnetty, wHuomiot] = COL_WIDTHS;
  const lupatunnusLines = wrapLines(ctx, row.lupatunnus || "", wLupatunnus - CELL_PAD_X * 2 - COL_GUTTER, "20px Lato, sans-serif");
  const tyyppiLines = wrapLines(ctx, row.tyyppi || "", wTyyppi - CELL_PAD_X * 2 - COL_GUTTER, "bold 20px Lato, sans-serif");
  const lahdeLines = row.lahde ? wrapLines(ctx, row.lahde, wTyyppi - CELL_PAD_X * 2 - COL_GUTTER, "17px Lato, sans-serif") : [];
  const myonnettyLines = wrapLines(ctx, row.myonnetty || "", wMyonnetty - CELL_PAD_X * 2 - COL_GUTTER, "20px Lato, sans-serif");
  const huomiotLines = wrapLines(ctx, row.huomiot || "", wHuomiot - CELL_PAD_X * 2 - COL_GUTTER, "20px Lato, sans-serif");

  const tyyppiColH = tyyppiLines.length * LINE_H + (lahdeLines.length ? lahdeLines.length * LINE_H_SMALL + 3 : 0);
  const otherMaxLines = Math.max(lupatunnusLines.length, myonnettyLines.length, huomiotLines.length);
  const height = Math.max(34, tyyppiColH, otherMaxLines * LINE_H) + CELL_PAD_Y * 2;

  return { height, lines: { lupatunnusLines, tyyppiLines, lahdeLines, myonnettyLines, huomiotLines } };
}

function drawTableHeader(ctx, x, y, headerPlan) {
  // Same green as the subtotal/footer bars in
  // renderToimenpiteetTekstimuodossa.js's drawSubtotalBar (#04aa00 — the
  // page's own top-banner green, not #198754) — no bottom stroke, matching
  // that bar's own plain-fill styling.
  ctx.fillStyle = "#04aa00";
  ctx.fillRect(x, y, USABLE_W, headerPlan.height);
  ctx.textAlign = "left";
  ctx.fillStyle = "#ffffff";
  let cx = x;
  headerPlan.lines.forEach((lines, i) => {
    lines.forEach((line, li) => {
      ctx.font = "bold 20px Lato, sans-serif";
      ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
    });
    cx += COL_WIDTHS[i];
  });
  return headerPlan.height;
}

function drawRow(ctx, x, y, lines, height) {
  ctx.textAlign = "left";
  let cx = x;

  // Col 0: Lupatunnus
  ctx.fillStyle = "#1c1c1e";
  lines.lupatunnusLines.forEach((line, li) => {
    ctx.font = "20px Lato, sans-serif";
    ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
  });
  cx += COL_WIDTHS[0];

  // Col 1: Tyyppi (bold) + lähde (muted) below.
  ctx.fillStyle = "#1c1c1e";
  lines.tyyppiLines.forEach((line, li) => {
    ctx.font = "bold 20px Lato, sans-serif";
    ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
  });
  const lahdeY0 = y + CELL_PAD_Y + lines.tyyppiLines.length * LINE_H + 3;
  ctx.fillStyle = "#8e8e93";
  lines.lahdeLines.forEach((line, li) => {
    ctx.font = "17px Lato, sans-serif";
    ctx.fillText(line, cx + CELL_PAD_X, lahdeY0 + (li + 1) * LINE_H_SMALL - 2);
  });
  cx += COL_WIDTHS[1];

  // Col 2: Myönnetty
  ctx.fillStyle = "#1c1c1e";
  lines.myonnettyLines.forEach((line, li) => {
    ctx.font = "20px Lato, sans-serif";
    ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
  });
  cx += COL_WIDTHS[2];

  // Col 3: Huomiot
  ctx.fillStyle = "#1c1c1e";
  lines.huomiotLines.forEach((line, li) => {
    ctx.font = "20px Lato, sans-serif";
    ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
  });

  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + USABLE_W, y + height);
  ctx.stroke();
}

function buildChunkImage(rowEntries, headerPlan) {
  const contentH = rowEntries.reduce((sum, e) => sum + e.height, 0);
  const totalH = PAD_Y * 2 + headerPlan.height + contentH;

  const canvas = document.createElement("canvas");
  canvas.width = IMG_WIDTH;
  canvas.height = totalH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let cursorY = PAD_Y;
  cursorY += drawTableHeader(ctx, PAD_X, cursorY, headerPlan);

  for (const entry of rowEntries) {
    drawRow(ctx, PAD_X, cursorY, entry.lines, entry.height);
    cursorY += entry.height;
  }

  return canvas.toDataURL("image/png");
}

function drawRakennuslupaTaulukko({ data, content, contentWidth = CONTENT_WIDTH }) {
  const { rows = [] } = data || {};
  if (!rows.length) return;

  const measureCanvas = document.createElement("canvas");
  const mctx = measureCanvas.getContext("2d");
  const headerPlan = measureHeader(mctx);

  const allEntries = rows.map(row => {
    const { height, lines } = measureRow(mctx, row);
    return { height, lines };
  });

  const chunks = [];
  let current = [];
  let currentH = headerPlan.height;

  for (const entry of allEntries) {
    const wouldFit = currentH + entry.height <= MAX_CHUNK_H;
    if (!wouldFit && current.length) {
      chunks.push(current);
      current = [];
      currentH = headerPlan.height;
    }
    current.push(entry);
    currentH += entry.height;
  }
  if (current.length) chunks.push(current);

  chunks.forEach((entries, idx) => {
    const imageBase64 = buildChunkImage(entries, headerPlan);
    content.push({
      image: imageBase64,
      width: contentWidth,
      margin: [0, 0, 0, idx === chunks.length - 1 ? bottomMarginItemGlobal : 4],
    });
  });
}

// Fetches live data and draws it — this block no longer carries its own
// frozen copy in the report's content (see RakennuslupaTaulukko.jsx's own
// comment on why). A kiinteistö with no permits recorded yet renders
// nothing, same as an empty `data.rows` did before.
export async function renderRakennuslupaTaulukko({ kiinteistoId, content, contentWidth = CONTENT_WIDTH }) {
  if (!kiinteistoId) return;
  const { data: rows } = await getRakennusluvatByKiinteisto(kiinteistoId);
  drawRakennuslupaTaulukko({ data: { rows }, content, contentWidth });
}
